import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash2, Package, Pencil } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = ({ onLogout }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    margins: '',
    differentiated: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Edit state
  const [editProduct, setEditProduct] = useState(null);
  const [editData, setEditData] = useState({ name: '', margins: '', differentiated: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/products`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product created successfully!');
      setFormData({ name: '', margins: '', differentiated: '' });
      setShowForm(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOpen = (product) => {
    setEditProduct(product);
    setEditData({
      name: product.name,
      margins: product.margins,
      differentiated: product.differentiated || 'No'
    });
  };

  const handleEditSubmit = async () => {
    if (!editData.name || !editData.margins || !editData.differentiated) {
      toast.error('Please fill all fields');
      return;
    }
    setEditSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/products/${editProduct.id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product updated successfully!');
      setEditProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-8" data-testid="products-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Product Master
            </h1>
            <p className="text-base text-slate-600 mt-2">Manage your product catalog</p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-gradient-to-r from-teal-700 to-emerald-500 hover:from-teal-800 hover:to-emerald-600 text-white shadow-lg shadow-teal-500/25 rounded-xl px-6 py-3 font-semibold transition-all active:scale-95"
            data-testid="add-product-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Product
          </Button>
        </div>

        {showForm && (
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 shadow-xl p-6 mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Add New Product
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Product Name *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-2 h-12 border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 rounded-lg"
                    data-testid="product-name-input"
                    placeholder="Enter product name"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Margins *
                  </Label>
                  <Select
                    value={formData.margins}
                    onValueChange={(value) => setFormData({ ...formData, margins: value })}
                    required
                  >
                    <SelectTrigger className="mt-2 h-12" data-testid="margins-select">
                      <SelectValue placeholder="Select margins" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Differentiated ? *
                  </Label>
                  <Select
                    value={formData.differentiated}
                    onValueChange={(value) => setFormData({ ...formData, differentiated: value })}
                    required
                  >
                    <SelectTrigger className="mt-2 h-12" data-testid="differentiated-select">
                      <SelectValue placeholder="Select differentiated" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center space-x-4 pt-4">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-gradient-to-r from-teal-700 to-emerald-500 hover:from-teal-800 hover:to-emerald-600 text-white shadow-lg shadow-teal-500/25 rounded-xl px-6 py-3 font-semibold transition-all active:scale-95"
                  data-testid="submit-product-button"
                >
                  {submitting ? 'Creating...' : 'Create Product'}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowForm(false)}
                  variant="outline"
                  className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-xl"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 shadow-xl">
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-teal-500 border-t-transparent"></div>
                <p className="text-slate-500 mt-4">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No products yet</p>
                <Button
                  onClick={() => setShowForm(true)}
                  variant="outline"
                  className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50"
                >
                  Add your first product
                </Button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="products-list">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between p-5 border border-slate-200 rounded-xl hover:border-teal-200 hover:shadow-md transition-all"
                    data-testid="product-card"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-lg">
                        <Package className="h-5 w-5 text-teal-700" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{product.name}</h3>
                        <div className="flex items-center space-x-3 mt-1">
                          <span className={`text-xs px-2 py-1 rounded-md font-medium ${
                            product.margins === 'High' 
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                              : 'bg-amber-50 text-amber-700 border border-amber-100'
                          }`}>
                            Margins: {product.margins}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-md font-medium ${
                            product.differentiated === 'Yes' 
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                              : 'bg-amber-50 text-amber-700 border border-amber-100'
                          }`}>
                            Differentiated: {product.differentiated}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleEditOpen(product)}
                        variant="ghost"
                        size="sm"
                        className="text-teal-700 hover:text-teal-700 hover:bg-teal-50"
                        data-testid="edit-product-button"
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDelete(product.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="delete-product-button"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Edit Product Modal */}
        <Dialog open={!!editProduct} onOpenChange={(open) => { if (!open) setEditProduct(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Edit Product</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Product Name *</Label>
                <Input
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="mt-1 h-12"
                  data-testid="edit-product-name-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Margins *</Label>
                <Select value={editData.margins} onValueChange={(value) => setEditData({ ...editData, margins: value })}>
                  <SelectTrigger className="mt-1 h-12" data-testid="edit-margins-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Differentiated ? *</Label>
                <Select value={editData.differentiated} onValueChange={(value) => setEditData({ ...editData, differentiated: value })}>
                  <SelectTrigger className="mt-1 h-12" data-testid="edit-differentiated-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditProduct(null)}>Cancel</Button>
                <Button
                  onClick={handleEditSubmit}
                  disabled={editSubmitting}
                  className="bg-gradient-to-r from-teal-700 to-emerald-500 text-white"
                  data-testid="save-edit-product-button"
                >
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Products;
